import { FhirBundleSdk } from "@metriport/fhir-sdk";
import { TableRow } from "../utils/tables-metadata";
import { AllergyTable } from "./allergy";
import { CarePlanTable } from "./care-plan";
import { CareTeamTable } from "./care-team";
import { CareGapsTable } from "./care-gaps/care-gaps-table";
import { CareGapRowData } from "./care-gaps/care-gaps-rows";
import { ConditionTable } from "./condition";
import { CoverageTable } from "./coverage";
import { EncounterTable } from "./encounter";
import { FamilyHistoryTable } from "./family-history";
import { ImmunizationTable } from "./immunization";
import { MedicationTable } from "./medication";
import { ProcedureTable } from "./procedure";
import { RelatedPersonTable } from "./related-person";
import { SocialHistoryTable } from "./social-history";
import { SurveyTable } from "./survey";
import { SuspectTable } from "./suspect/suspect-table";
import { SuspectRowData } from "./suspect/suspect-rows";
import { VitalTable } from "./vital";

export type SharedTableProps = {
  fhirSdk: FhirBundleSdk;
  filterQuery: string;
  onPdfView: (docId: string) => void;
  onRowClick: (row: TableRow) => void;
  isRowViewed: (row: TableRow) => boolean;
};

export type RenderSharedTableParams = {
  tableType: string;
  displayName: string;
  rows: TableRow[];
  commonProps: SharedTableProps;
};

/**
 * Renders table components that are shared between browse and search modes.
 * Returns the appropriate table component, or null if the tableType requires
 * mode-specific handling (e.g., DiagnosticReport, ImagingReports, Labs, DocumentReference).
 *
 * Mode-specific table types that return null:
 * - DiagnosticReport: Uses BrowseNotesTable vs SearchNotesTable
 * - ImagingReports: Uses BrowseImagingReportsTable vs SearchImagingReportsTable
 * - LaboratoryObservations: Uses LabTable (with toggle) vs IndividualLabTable
 * - LaboratoryObservationsPanel: Only exists in search mode
 * - DocumentReference: Has different filterQuery handling
 */
export function renderSharedTable({
  tableType,
  displayName,
  rows,
  commonProps,
}: RenderSharedTableParams): JSX.Element | null {
  switch (tableType) {
    case "AllergyIntolerance":
      return <AllergyTable rows={rows} displayName={displayName} {...commonProps} />;

    case "Procedure":
      return <ProcedureTable rows={rows} displayName={displayName} {...commonProps} />;

    case "Immunization":
      return <ImmunizationTable rows={rows} displayName={displayName} {...commonProps} />;

    case "FamilyMemberHistory":
      return <FamilyHistoryTable rows={rows} displayName={displayName} {...commonProps} />;

    case "RelatedPerson":
      return <RelatedPersonTable rows={rows} displayName={displayName} {...commonProps} />;

    case "Coverage":
      return <CoverageTable rows={rows} displayName={displayName} {...commonProps} />;

    case "Encounter":
      return <EncounterTable rows={rows} displayName={displayName} {...commonProps} />;

    case "CareTeam":
      return <CareTeamTable rows={rows} displayName={displayName} {...commonProps} />;

    case "Condition":
      return <ConditionTable rows={rows} displayName={displayName} {...commonProps} />;

    case "Medication":
      return <MedicationTable rows={rows} displayName={displayName} {...commonProps} />;

    case "VitalSignObservations":
      return <VitalTable rows={rows} displayName={displayName} {...commonProps} />;

    case "SurveysObservations":
      return <SurveyTable rows={rows} {...commonProps} />;

    case "SocialHistoryObservations":
      return <SocialHistoryTable rows={rows} displayName={displayName} {...commonProps} />;

    case "CarePlan":
      return <CarePlanTable rows={rows} displayName={displayName} {...commonProps} />;

    case "CareGaps":
      return (
        <CareGapsTable
          rows={rows as CareGapRowData[]}
          filterQuery={commonProps.filterQuery}
          onRowClick={commonProps.onRowClick}
          isRowViewed={commonProps.isRowViewed}
        />
      );

    case "Suspects":
      return (
        <SuspectTable
          rows={rows as SuspectRowData[]}
          displayName={displayName}
          filterQuery={commonProps.filterQuery}
          onRowClick={commonProps.onRowClick}
          isRowViewed={commonProps.isRowViewed}
        />
      );

    default:
      // Return null for mode-specific table types or unknown types
      return null;
  }
}

/**
 * Table types that are handled by renderSharedTable.
 * Used for metadata organization.
 */
export const SHARED_TABLE_TYPES = [
  "AllergyIntolerance",
  "Procedure",
  "Immunization",
  "FamilyMemberHistory",
  "RelatedPerson",
  "Coverage",
  "Encounter",
  "CareTeam",
  "Condition",
  "Medication",
  "VitalSignObservations",
  "SurveysObservations",
  "SocialHistoryObservations",
  "CarePlan",
  "CareGaps",
  "Suspects",
] as const;

export type SharedTableType = (typeof SHARED_TABLE_TYPES)[number];
